import { isLocalDataBackedMailboxInfo } from 'owa-account-source-list/lib/utils/isLocalDataBackedAccount';
import { getItemDataBase64Blob } from '../utils/itemBase64DataForPST';
import { getDownloadMessageUrl } from 'owa-files-url/lib/actions/getDownloadMessageUrl';
import mailStore from 'owa-mail-store/lib/store/Store';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import type ClientItem from 'owa-mail-store/lib/store/schema/ClientItem';
import { isFeatureEnabled } from 'owa-feature-flags';
import getItemRightsManagementRestrictions from 'owa-mail-store/lib/utils/getItemRightsManagementRestrictions';
import { getRootItemId } from 'owa-root-item-store';
import { isDownloadAllowed } from 'owa-attachment-user-operations/lib/utils/isDownloadAllowed';
import type Message from 'owa-service/lib/contract/Message';
import type { MailboxInfo } from 'owa-client-types';

/**
 * Handles drag data for a mail list items, including PST and non-PST logic.
 */
export function dragToDownloadEmails(
    dataTransfer: DataTransfer,
    itemIds: string[],
    subjects: string[],
    mailboxInfos: MailboxInfo[]
): void {
    if (!isFeatureEnabled('mon-dnd-download')) {
        return;
    }
    const numItems = isFeatureEnabled('mon-dnd-download-multiitem')
        ? itemIds.length
        : Math.min(1, itemIds.length);

    const dndURLs: string[] = [];
    for (let i = 0; i < numItems; i++) {
        const itemId = itemIds[i];
        const subject = subjects[i];
        const mailboxInfo = mailboxInfos[i];

        let pstBlobData;
        if (isLocalDataBackedMailboxInfo(mailboxInfo)) {
            // If data was not loaded, just skip since we know that it won't work
            // and we don't want to show a a possible download link that downloads won't work
            // We are loading just the visible items in the ListView and for conversations
            // that means the latest item, so if other items are dragged, we won't download
            // This is because we don't want to load a lot of blobs in memory
            const data = getItemDataBase64Blob(itemId);
            if (data) {
                pstBlobData = {
                    base64DataUrl: data.base64DataUrl,
                    mimeType: data.mimeType,
                };
            }
        }
        const url = getSingleDownloadURLData(itemId, subject, pstBlobData);
        if (url) {
            dndURLs.push(url);
        }
    }
    if (dndURLs.length > 0) {
        if (isFeatureEnabled('mon-dnd-download-multiitem')) {
            dataTransfer.setData('DownloadURL', JSON.stringify(dndURLs));
        } else {
            dataTransfer.setData('DownloadURL', dndURLs[0]);
        }
    }
}

function getSingleDownloadURLData(
    itemId: string,
    subject: string | undefined,
    pstBlobData?: {
        base64DataUrl: string;
        mimeType: string;
    }
): string {
    const item = mailStore().items.get(itemId);
    const mimeType = pstBlobData?.mimeType || 'application/octet-stream';
    const fileName = (item?.Subject || subject || 'File') + (pstBlobData ? '.msg' : '.eml'); // PST only supports .msg
    const IRMRestrictions = getItemRightsManagementRestrictions(item);
    /*
        If fileName has a colon we replace all occurences of colon with empty string as colon is not allowed in file name,
        dndDownload will be of the format mimeType:fileName:downloadURLWithUpdatedToken. An extra colon within the file name will break the format.
            */
    const sanitizedFileName = fileName.split(':').join('');
    const mailboxInfo = (item as ClientItem)?.MailboxInfo ?? getGlobalSettingsAccountMailboxInfo();
    const fullDownloadUrl: string | undefined =
        pstBlobData?.base64DataUrl || getDownloadMessageUrl(itemId, 'EML', mailboxInfo);
    const isEmailDownloadDisabled = item ? !allowDownload(item, mailboxInfo) : false;
    const isSaveAsAllowedByIRM = IRMRestrictions.EditAllowed;
    const isDownloadDisabled =
        !isSaveAsAllowedByIRM || isEmailDownloadDisabled || !!getRootItemId(itemId);
    if (!isDownloadDisabled && fullDownloadUrl) {
        return [mimeType, sanitizedFileName, fullDownloadUrl].join(':');
    }
    return '';
}

function allowDownload(message: Message, mailboxInfo: MailboxInfo): boolean {
    return (
        !message.Attachments ||
        !!message.Attachments?.every(attachment => isDownloadAllowed(attachment, mailboxInfo))
    );
}
